/**
 * Full-screen presentation mode for a generated report — the DOM logic.
 *
 * A report is an AI-authored HTML document rendered inside an iframe. Presentation
 * mode turns that document into a deck: one section on screen at a time, centred,
 * stepped through with the keyboard, with everything else hidden.
 *
 * ## Why the sections are discovered and not declared
 *
 * The obvious implementation asks the report for its sections. Nothing can: report
 * HTML is generated by a model against a template that has changed several times,
 * and reports already sitting in S3 were written by every version of it. A mode that
 * only worked on documents generated after it shipped would be a mode nobody could
 * use on the reports they actually have.
 *
 * So {@link discoverSlides} reads the structure that is really there, in four
 * descending tiers of confidence, and stops at the first that produces a usable
 * result. The last tier is the one that matters, because it is the one that fires
 * for an arbitrary document: group the content container's own children into runs
 * that each start at a heading. That needs no markup contract at all — only that
 * the report has headings, which every report has, because a report without
 * headings is not a report.
 *
 * ## Why nothing is restructured
 *
 * Slides are expressed by stamping `data-sai-slide` on elements that already exist
 * and letting one injected stylesheet hide the rest. Wrapping runs of siblings in
 * new containers would be the tidier data structure and it is not worth it: report
 * CSS is full of `>` child combinators and `:first-child` rules written against the
 * original tree, and a wrapper silently breaks them. Stamping an attribute cannot.
 * It also makes exit trivial and total — remove the attributes and the stylesheet,
 * and the document is byte-identical to what it was.
 *
 * Pure DOM, no Angular: every function here takes a `Document` and returns data, so
 * the whole thing is testable against a real document in a real browser.
 */

/** One step of the presentation. */
export interface ReportSlide {
    /** Zero-based position, and the value stamped into `data-sai-slide`. */
    index: number;
    /** Heading text for the chrome, already trimmed and collapsed. */
    title: string;
    /** Heading depth (1-6), or 0 when the slide came from a container with no heading. */
    level: number;
}

/** Marks an element as belonging to a slide. Removed completely on exit. */
const SLIDE_ATTR = 'data-sai-slide';

/**
 * Marks the container whose children the slides were cut from.
 *
 * Needed because "hide everything that is not the current slide" is not the same
 * rule as "hide the other slides". A real report was verified against and its
 * `<footer>` is neither chrome nor a section, so it belonged to no slide and sat
 * under every one of them. Anything else the discovery did not claim — a trailing
 * script wrapper, a back-to-top block, a template's stray div — has the same
 * problem, and enumerating them would be a list that is wrong for the next report.
 * Scoping to the stage and hiding the complement is the rule that cannot miss one.
 */
const STAGE_ATTR = 'data-sai-stage';

/**
 * Marks every element between the stage and `<body>`, `<body>` included.
 *
 * The stage rule above only reaches the stage's OWN children, which is enough when
 * the stage is the body and nothing when it is not. The product's default report
 * theme puts its sections in `<main>` and leaves the cover `<section>` and the
 * `<footer>` outside it as siblings of that `<main>`: they are on no stage, belong
 * to no slide, and were therefore left on screen under every slide, with the cover
 * alone tall enough to fill the viewport. Marking the ancestor chain lets the same
 * "hide the complement" rule apply at every level between the stage and the body,
 * so where the content root sits in the document stops mattering.
 */
const STAGE_PATH_ATTR = 'data-sai-stage-path';

/** Set on `<html>` while presenting, so every rule below is scoped to that state. */
export const PRESENT_ATTR = 'data-sai-presenting';

/** Id of the injected stylesheet, so it can be found and removed on exit. */
const STYLE_ID = 'sai-report-presentation';

/**
 * Elements that are never a slide of their own.
 *
 * A report's own fixed navigation bar and its skip links are chrome, not content.
 * Left in the flow they would become slide 1 and the presentation would open on a
 * navigation menu.
 */
const CHROME_SELECTOR =
    'nav, header[role="banner"], .report-nav, .skip-link, script, style, link, template, noscript';

/**
 * Longest run of content a single slide may hold before it is allowed to scroll.
 *
 * Not a limit on what is shown — nothing is ever hidden inside a slide — but the
 * point past which the slide stops being vertically centred and starts at the top,
 * because centring something taller than the viewport hides its beginning.
 */
export const SLIDE_SCROLL_THRESHOLD_RATIO = 0.95;

const clean = (value: string | null | undefined): string =>
    (value ?? '').replace(/\s+/g, ' ').trim();

/** The element a report's content actually lives in. */
function contentRoot(doc: Document): HTMLElement | null {
    const candidates = [
        '[data-report-content]',
        'main',
        'article',
        '.report-body',
        '.report-content',
        '#report',
    ];
    for (const selector of candidates) {
        const found = doc.querySelector<HTMLElement>(selector);
        // A container that holds nothing is not the content root, whatever it is
        // called — some templates emit an empty `<main>` beside the real body.
        if (found && found.children.length > 0) return found;
    }
    return doc.body ?? null;
}

const isChrome = (el: Element): boolean => el.matches(CHROME_SELECTOR);

/** The first heading at or inside an element, for a slide's title. */
function headingOf(el: Element): { title: string; level: number } | null {
    const heading = el.matches('h1,h2,h3,h4,h5,h6')
        ? el
        : el.querySelector('h1,h2,h3,h4,h5,h6');
    if (!heading) return null;
    const title = clean(heading.textContent);
    if (!title) return null;
    return { title, level: Number(heading.tagName.slice(1)) || 0 };
}

/**
 * Stamp `groups` onto the document and return the slide list.
 *
 * Every element of a group carries the same index, which is what lets a slide span
 * several siblings without any of them being moved.
 */
function stamp(groups: Element[][], stage: Element | null): ReportSlide[] {
    // The stage is whichever element the slides are children of, which is not always
    // the content root: tier 4 cuts from one level deeper. Read off the first group
    // rather than passed down, so a new tier cannot forget to set it.
    const resolvedStage = stage ?? groups[0]?.[0]?.parentElement ?? null;
    resolvedStage?.setAttribute(STAGE_ATTR, 'true');
    markStagePath(resolvedStage);

    const slides: ReportSlide[] = [];
    groups.forEach((group, index) => {
        for (const el of group) el.setAttribute(SLIDE_ATTR, String(index));
        const heading = group.map(headingOf).find(Boolean) ?? null;
        slides.push({
            index,
            title: heading?.title || `Section ${index + 1}`,
            level: heading?.level ?? 0,
        });
    });
    return slides;
}

/**
 * Mark the ancestors of the stage, up to and including `<body>`.
 *
 * Stops at `<body>` rather than `<html>`: the head's children are not rendered, and
 * hiding them would be a rule that reads as if it did something.
 */
function markStagePath(stage: Element | null): void {
    const root = stage?.ownerDocument?.documentElement ?? null;
    for (let el = stage?.parentElement ?? null; el && el !== root; el = el.parentElement) {
        el.setAttribute(STAGE_PATH_ATTR, 'true');
    }
}

/**
 * Content that sits before the content root, as a slide of its own.
 *
 * The default report theme's cover is a `<section>` OUTSIDE the `<main>` the sections
 * live in, so section discovery never sees it. Without this it would simply vanish
 * from the presentation, and a deck that drops the report's title and headline
 * figures is missing its first slide. It is the same rule tier 3 already applies to
 * a lead-in inside the content root, applied one level out.
 *
 * Only what comes BEFORE the root: what comes after is a footer or a script wrapper
 * in every template seen so far, and boilerplate is not a closing slide.
 */
function leadInBefore(root: Element): Element[] {
    if (root === root.ownerDocument?.body) return [];
    const before: Element[] = [];
    for (let el = root.previousElementSibling; el; el = el.previousElementSibling) {
        if (!isChrome(el) && clean(el.textContent).length > 0) before.unshift(el);
    }
    return before;
}

/**
 * Split a container's children into runs that each begin at a heading.
 *
 * The tier that works on an arbitrary document. Anything before the first heading
 * (a cover block, a KPI row, an intro paragraph) becomes slide 1 on its own, because
 * it is the report's opening and folding it into the first real section would bury
 * it.
 */
function groupByHeadings(children: Element[], levels: string): Element[][] {
    const groups: Element[][] = [];
    let current: Element[] = [];

    for (const child of children) {
        const startsSlide = child.matches(levels) || !!child.querySelector(`:scope > ${levels}`);
        if (startsSlide && current.length) {
            groups.push(current);
            current = [];
        }
        current.push(child);
    }
    if (current.length) groups.push(current);
    return groups;
}

/**
 * Find the report's slides and stamp them onto the document.
 *
 * Returns an empty list when the document has no usable structure at all, which the
 * caller must treat as "presentation is not available for this report" rather than
 * as an error — a one-page report with no headings is a legitimate document that
 * simply has nothing to step through.
 */
export function discoverSlides(doc: Document): ReportSlide[] {
    const root = contentRoot(doc);
    if (!root) return [];

    clearSlideMarks(doc);

    const children = Array.from(root.children).filter(
        el => !isChrome(el) && clean(el.textContent).length > 0,
    );
    if (!children.length) return [];

    // Every tier stamps through the same wrapper, so a cover sitting outside the
    // content root reaches the deck whichever tier claimed the sections.
    const cut = (groups: Element[][], stage: Element | null): ReportSlide[] => {
        const resolved = stage ?? groups[0]?.[0]?.parentElement ?? null;
        const lead = leadInBefore(root);
        return stamp(lead.length ? [lead, ...groups] : groups, resolved);
    };

    // Tier 1 — the report said so. Nothing generated today does, and that is fine:
    // this is the contract a future template can adopt to take full control.
    const declared = Array.from(root.querySelectorAll<HTMLElement>('[data-report-slide]'));
    if (declared.length > 1) return cut(declared.map(el => [el]), null);

    // Tier 2 — real `<section>` elements, the commonest shape in the current template.
    const sections = children.filter(el => el.tagName === 'SECTION');
    if (sections.length > 1) return cut(sections.map(el => [el]), root);

    // Tier 3 — headings at the top level of the content root.
    const byH2 = groupByHeadings(children, 'h1,h2');
    if (byH2.length > 1) return cut(byH2, root);

    // Tier 4 — the whole document is one block, so look one level deeper. This is
    // the case where a template wraps everything in a single `<div class="container">`.
    const only = children.length === 1 ? children[0] : null;
    if (only) {
        const inner = Array.from(only.children).filter(
            el => !isChrome(el) && clean(el.textContent).length > 0,
        );
        const innerSections = inner.filter(el => el.tagName === 'SECTION');
        if (innerSections.length > 1) return cut(innerSections.map(el => [el]), only);
        const innerGroups = groupByHeadings(inner, 'h1,h2,h3');
        if (innerGroups.length > 1) return cut(innerGroups, only);
    }

    return [];
}

/**
 * The stylesheet that turns a stamped document into a deck.
 *
 * Every rule is `!important` and scoped to `[data-sai-presenting]`, for two reasons
 * that are really one: report CSS is model-authored, arbitrary, and frequently
 * inline, so a non-important rule loses to it unpredictably; and the whole
 * stylesheet vanishes the instant presentation ends, so nothing here can leak into
 * ordinary viewing.
 */
export const PRESENTATION_CSS = `
html[${PRESENT_ATTR}], html[${PRESENT_ATTR}] body {
  height: 100% !important;
  overflow: hidden !important;
  scroll-behavior: auto !important;
}
/* The report's own fixed navigation is chrome and would float over the slide. */
html[${PRESENT_ATTR}] ${CHROME_SELECTOR.split(', ')
    .map(s => s.trim())
    .filter(s => !['script', 'style', 'link', 'template', 'noscript'].includes(s))
    .join(', html[' + PRESENT_ATTR + '] ')} {
  display: none !important;
}
html[${PRESENT_ATTR}] [${SLIDE_ATTR}] {
  display: none !important;
}
/* Everything on the stage that belongs to NO slide. A real report's <footer> is
   neither chrome nor a section, so without this it sits under every slide; the
   same is true of anything else a template emits that discovery did not claim. */
html[${PRESENT_ATTR}] [${STAGE_ATTR}] > *:not([${SLIDE_ATTR}]) {
  display: none !important;
}
/* The same complement, at every level between the stage and the body. The default
   report theme keeps its cover and its footer OUTSIDE the <main> the sections live
   in, so the rule above cannot reach them and the cover alone is tall enough to be
   the only thing on screen. A slide is excluded, which is what lets a cover that
   was promoted to slide 1 still show when it is the active one. */
html[${PRESENT_ATTR}] [${STAGE_PATH_ATTR}]
  > *:not([${STAGE_PATH_ATTR}]):not([${STAGE_ATTR}]):not([${SLIDE_ATTR}]) {
  display: none !important;
}
html[${PRESENT_ATTR}] [${SLIDE_ATTR}].sai-slide-active {
  display: block !important;
}
/* The slide stage: a full-viewport scroll container that centres content short
   enough to be centred and lets anything taller scroll from its top. */
html[${PRESENT_ATTR}] body {
  display: block !important;
  padding: clamp(24px, 4vh, 56px) clamp(20px, 5vw, 96px) !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  overflow-y: auto !important;
}
html[${PRESENT_ATTR}] body.sai-slide-centred {
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  overflow-y: hidden !important;
}
/* Room for the presenter chrome, which is drawn by the parent document over the
   bottom of the frame. Without this the last line of a slide sits under it. */
html[${PRESENT_ATTR}] body {
  padding-bottom: 104px !important;
}
/* A slide arrives rather than appearing, which makes the step direction legible.
   Suppressed entirely under reduced motion — a presentation is exactly the context
   where repeated movement is most likely to be a problem for somebody. */
@media (prefers-reduced-motion: no-preference) {
  html[${PRESENT_ATTR}] [${SLIDE_ATTR}].sai-slide-active {
    animation: sai-slide-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  @keyframes sai-slide-in {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: none; }
  }
}
`;

/** Inject the presentation stylesheet, once. */
export function applyPresentationStyles(doc: Document): void {
    if (doc.getElementById(STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = PRESENTATION_CSS;
    (doc.head ?? doc.documentElement).appendChild(style);
}

/**
 * Does the active slide's own content fit the viewport?
 *
 * Measures what the ACTIVE SLIDE puts on the stage, never the body, and that
 * distinction is the whole function. The presentation stylesheet gives `body`
 * `height: 100%`, so
 * `body.scrollHeight` is at least the viewport height whatever is inside it — read
 * that way, every slide looks too tall, the centred branch never fires, and a
 * one-line closing slide sits jammed against the top of the screen. Verified in the
 * browser against a real report: a 97px slide in a 720px viewport reported a
 * `body.scrollHeight` of 720 and was not centred.
 *
 * What is measured is each active slide's OUTERMOST visible block — the body-level
 * element it sits inside, which is the slide itself when the sections are children
 * of the body, and the `<main>` around it in the default theme. That element is what
 * the body actually lays out and centres, and it brings its own padding: measuring
 * the slide alone under a `<main class="py-12">` under-reads the real height by most
 * of the slack this threshold allows, and a centred slide that does not fit is
 * clipped at the top with nothing to scroll, because centring turns scrolling off.
 *
 * They are SUMMED because a slide can span several siblings (the heading-run tiers
 * produce exactly that), and de-duplicated because those siblings usually share one
 * outermost block. The body's own vertical padding is added because it is space the
 * slide has to share the viewport with.
 */
function slideFitsViewport(doc: Document, body: HTMLElement): boolean {
    const view = doc.defaultView;
    const viewport = view?.innerHeight ?? 0;
    if (!viewport) return false;

    const active = Array.from(doc.querySelectorAll<HTMLElement>('.sai-slide-active'));
    if (!active.length) return false;

    const blocks = new Set<HTMLElement>();
    for (const el of active) {
        const block = outermostBlock(el, body);
        if (block) blocks.add(block);
    }
    if (!blocks.size) return false;

    const styles = view?.getComputedStyle(body);
    const padding =
        (parseFloat(styles?.paddingTop ?? '0') || 0) +
        (parseFloat(styles?.paddingBottom ?? '0') || 0);
    let content = 0;
    for (const block of blocks) content += block.scrollHeight;

    return content + padding <= viewport * SLIDE_SCROLL_THRESHOLD_RATIO;
}

/** The ancestor of `el` that is a direct child of `body`, or `el` when it is one. */
function outermostBlock(el: HTMLElement, body: HTMLElement): HTMLElement | null {
    let block: HTMLElement | null = el;
    while (block && block.parentElement && block.parentElement !== body) {
        block = block.parentElement;
    }
    return block?.parentElement === body ? block : null;
}

/**
 * Show one slide.
 *
 * Also decides whether this particular slide is centred or top-aligned, which has
 * to be measured rather than assumed: a two-line summary and a twelve-row table are
 * both slides, and centring the second would hide its heading above the fold.
 */
export function setActiveSlide(doc: Document, index: number): void {
    const body = doc.body;
    if (!body) return;

    for (const el of Array.from(doc.querySelectorAll(`[${SLIDE_ATTR}]`))) {
        if (el.getAttribute(SLIDE_ATTR) === String(index)) el.classList.add('sai-slide-active');
        else dropClass(el, 'sai-slide-active');
    }

    // Measured AFTER the class flip, so the height read is this slide's own.
    dropClass(body, 'sai-slide-centred');
    if (slideFitsViewport(doc, body)) body.classList.add('sai-slide-centred');
    body.scrollTop = 0;
    doc.defaultView?.scrollTo?.(0, 0);
}

/**
 * Drop a class, and drop the `class` attribute with it when nothing is left.
 *
 * `classList.remove` on an element that never had a `class` attribute leaves
 * `class=""` behind. Visually that is nothing, and it is still residue: it means a
 * round trip through presentation mode does not return the document to what it was,
 * which is the property the whole clean-up design rests on. An empty `class` is
 * semantically identical to no `class`, so removing it can never lose anything.
 */
function dropClass(el: Element, name: string): void {
    if (!el.classList.contains(name)) {
        // Never touch an element the mode did not mark — `classList.remove` on an
        // absent class would still materialise the attribute.
        return;
    }
    el.classList.remove(name);
    if (el.classList.length === 0) el.removeAttribute('class');
}

/** Remove every slide mark, leaving the document exactly as it was found. */
export function clearSlideMarks(doc: Document): void {
    for (const el of Array.from(doc.querySelectorAll(`[${SLIDE_ATTR}]`))) {
        el.removeAttribute(SLIDE_ATTR);
        dropClass(el, 'sai-slide-active');
    }
    for (const el of Array.from(doc.querySelectorAll(`[${STAGE_ATTR}]`))) {
        el.removeAttribute(STAGE_ATTR);
    }
    for (const el of Array.from(doc.querySelectorAll(`[${STAGE_PATH_ATTR}]`))) {
        el.removeAttribute(STAGE_PATH_ATTR);
    }
    if (doc.body) dropClass(doc.body, 'sai-slide-centred');
}

/**
 * Leave presentation mode and undo everything it did.
 *
 * Total by construction: the attribute, the marks and the stylesheet are the only
 * three things presentation mode ever adds, and all three come off here. That is
 * what makes it safe to enter and leave repeatedly without the report drifting.
 */
export function exitPresentation(doc: Document): void {
    doc.documentElement?.removeAttribute(PRESENT_ATTR);
    clearSlideMarks(doc);
    doc.getElementById(STYLE_ID)?.remove();
}

/** Enter presentation mode on an already-stamped document. */
export function enterPresentation(doc: Document): void {
    applyPresentationStyles(doc);
    doc.documentElement?.setAttribute(PRESENT_ATTR, 'true');
}
